import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styles from './TextEditor.module.scss';
import './styles.css';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Label from '../label/Label';
import { ToolBar } from './tool-bar/ToolBar';
import { formats } from './tool-bar/ToolBar';
import debounce from 'lodash.debounce';
import { variableManagement } from 'hooks/endpoints';
import { useFetch } from 'hooks/useFetch';
import { useBrandingContext } from 'app-context/BrandingsContext';

const isArabicText = (text = '') =>
  /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/.test(text);

const stripHtml = (html = '') =>
  html
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&#39;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/\s+/g, ' ')
    .trim();

const TextEditor = ({
  label,
  placeholder,
  name,
  value,
  onChange,
  error,
  touched,
  isLoading,
  customFormats,
  className,
  isSave = false,
  isToolbarReduced = false,
  isCustom = false,
  onSaveClick,
  textEditorClassName,
}) => {
  const { brandingData } = useBrandingContext();
  const { page_TextColor } = brandingData;

  const quillRef = useRef(null);

  const [editorContent, setEditorContent] = useState(value || '');
  const [toolbarId] = useState(
    () => `toolbar-${Math.random().toString(36).substring(2, 15)}`,
  );

  const { data: allVariables } = useFetch(
    ['allVariables'],
    variableManagement.get,
    { enable: true },
    { pageNumber: 1, pageSize: 2000 },
  );

  useEffect(() => {
    setEditorContent(value || '');
  }, [value]);

  const plainText = useMemo(() => stripHtml(editorContent), [editorContent]);
  const isArabic = useMemo(() => isArabicText(plainText), [plainText]);

  useEffect(() => {
    const quill = quillRef.current?.getEditor?.();
    if (!quill) return;

    const root = quill.root;
    const dir = isArabic ? 'rtl' : 'ltr';

    root.setAttribute('dir', dir);
    root.style.textAlign = isArabic ? 'right' : 'left';
  }, [isArabic]);

  const debouncedOnChange = useCallback(
    debounce((content) => {
      if (onChange) onChange(content);
    }, 100),
    [onChange],
  );

  useEffect(() => {
    return () => {
      debouncedOnChange.cancel?.();
    };
  }, [debouncedOnChange]);

  const pushContent = useCallback(
    (content) => {
      setEditorContent(content);
      debouncedOnChange(content);
    },
    [debouncedOnChange],
  );

  const handleEditorChange = (content) => {
    pushContent(content);
  };

  const getModules = () => ({
    toolbar: { container: `#${toolbarId}` },
  });

  return (
    <div className={styles['editor-container']}>
      <Label text={label} />

      <div className={`textEditorCont ${className ?? ''}`}>
        <ToolBar
          quillRef={quillRef}
          setEditorContent={setEditorContent}
          editorContent={editorContent}
          onContentChange={pushContent}
          isSave={isSave}
          isCustom={isCustom ?? false}
          isToolbarReduced={isToolbarReduced ?? false}
          id={toolbarId}
          onSaveClick={onSaveClick}
          options={allVariables}
        />

        <ReactQuill
          ref={quillRef}
          value={isLoading ? '...' : editorContent}
          onChange={handleEditorChange}
          modules={getModules()}
          formats={customFormats ?? formats}
          placeholder={placeholder ?? 'Enter Content'}
          className={`${styles['text-editor']} ${textEditorClassName ?? ''}`}
          style={{ color: page_TextColor }}
        />
      </div>

      {error && touched && <div className='error-message'>{error}</div>}
    </div>
  );
};

export default TextEditor;
